import React, { useEffect, useState, useRef } from 'react';
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../ui/LatexRenderer';
import { Clock } from 'lucide-react';
export function ScheduleRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.interactiveConfig || {};
  const schedule = config.schedule || {
    columns: [],
    timeSlots: [],
    events: [] // Array of { col: number, row: number, content: string, color?: string }
  };
  const prevValidateRef = useRef(false);
  // Selection state: { col: number, row: number }
  const [selectedCell, setSelectedCell] = useState<{
    col: number;
    row: number;
  } | null>(null);
  // Reset state on question change
  useEffect(() => {
    setSelectedCell(null);
    prevValidateRef.current = false;
  }, [question.id]);
  // Reset validation flag when shouldValidate becomes false
  useEffect(() => {
    if (!shouldValidate) {
      prevValidateRef.current = false;
    }
  }, [shouldValidate]);
  // Notify parent of readiness
  useEffect(() => {
    onReadyChange?.(selectedCell !== null);
  }, [selectedCell, onReadyChange]);
  // Rising-edge detection for shouldValidate
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current && selectedCell) {
      prevValidateRef.current = true;
      // Return "row-col" string
      onAnswer(`${selectedCell.row}-${selectedCell.col}`);
    }
  }, [shouldValidate, selectedCell, onAnswer]);
  const handleCellClick = (row: number, col: number) => {
    if (selectedCell?.row === row && selectedCell?.col === col) {
      setSelectedCell(null);
    } else {
      setSelectedCell({
        row,
        col
      });
    }
  };
  // Helper to find event for a cell
  const getEvent = (row: number, col: number) => {
    return schedule.events.find((e: any) => e.row === row && e.col === col);
  };
  return <div className="w-full max-w-4xl mx-auto overflow-hidden rounded-xl border border-slate-200 shadow-sm bg-white" data-id="element-4865">
      <div className="overflow-x-auto" data-id="element-4866">
        <table className="w-full border-collapse min-w-[600px]" data-id="element-4867">
          <thead data-id="element-4868">
            <tr className="bg-slate-50 border-b border-slate-200" data-id="element-4869">
              <th className="p-3 w-20 border-r border-slate-200 bg-slate-100/50 sticky left-0 z-10" data-id="element-4870">
                <Clock className="w-5 h-5 text-slate-400 mx-auto" data-id="element-4871" />
              </th>
              {schedule.columns.map((col: string, i: number) => <th key={i} className="p-3 text-sm font-bold text-slate-700 text-center border-r border-slate-200 last:border-0 min-w-[100px]" data-id="element-4872">

                  <LatexRenderer content={col} data-id="element-4873" />
                </th>)}
            </tr>
          </thead>
          <tbody data-id="element-4874">
            {schedule.timeSlots.map((time: string, rowIndex: number) => <tr key={rowIndex} className="border-b border-slate-100 last:border-0" data-id="element-4875">

                {/* Time Column */}
                <td className="p-3 text-xs font-bold text-slate-500 text-center border-r border-slate-200 bg-slate-50/30 sticky left-0 z-10" data-id="element-4876">
                  {time}
                </td>

                {/* Data Columns */}
                {schedule.columns.map((_: any, colIndex: number) => {
              const event = getEvent(rowIndex, colIndex);
              const isSelected = selectedCell?.row === rowIndex && selectedCell?.col === colIndex;
              return <td key={colIndex} onClick={() => handleCellClick(rowIndex, colIndex)} className={`
                        p-2 border-r border-slate-100 last:border-0 cursor-pointer transition-all relative h-16
                        ${isSelected ? 'bg-indigo-50 ring-2 ring-inset ring-indigo-500 z-0' : 'hover:bg-slate-50'}
                      `} data-id="element-4877">

                      {event ? <div className={`
                            w-full h-full rounded-lg p-2 text-xs font-medium leading-tight flex items-center justify-center text-center
                            ${event.color === 'blue' ? 'bg-blue-100 text-blue-800' : ''}
                            ${event.color === 'green' ? 'bg-emerald-100 text-emerald-800' : ''}
                            ${event.color === 'orange' ? 'bg-orange-100 text-orange-800' : ''}
                            ${event.color === 'red' ? 'bg-rose-100 text-rose-800' : ''}
                            ${!event.color ? 'bg-slate-100 text-slate-700' : ''}
                          `} data-id="element-4878">

                          <LatexRenderer content={event.content} data-id="element-4879" />
                        </div> : <div className="w-full h-full" data-id="element-4880" />}
                    </td>;
            })}
              </tr>)}
          </tbody>
        </table>
      </div>
      <div className="bg-slate-50 p-3 text-center text-sm text-slate-500 border-t border-slate-200" data-id="element-4881">
        Klik op het juiste vakje in het rooster
      </div>
    </div>;
}